import { FormHandler } from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import {
  PurchaseGetCancelCalculationResponse,
  SupportRequestBackendClient,
} from '@naus-code/naus-admin-types';
import {
  BookingClientWithPurchasesAndTickets,
  PurchaseClientWithMetaWithTickets,
} from '@naus-code/naus-admin-types';
import { MutableRefObject } from 'react';
import { createGlobalState } from 'react-hooks-global-state';

import { CancellationMethod } from './Cancel.utils';

export const ModalActionPanelCancelBookingKey = 'ActionPanelCancelBooking';

export interface CancelBookingProps {
  bookingId: string;
  supportId: string;
  defaultPurchaseId?: string;
}

export interface CancelBookingPropsResolved extends CancelBookingProps {
  booking: BookingClientWithPurchasesAndTickets;
  supportRequest: SupportRequestBackendClient;
}

export interface CancelBookingFooterProps extends CancelBookingPropsResolved {
  formHandlerRef: MutableRefObject<FormHandler>;
  providerGroups: ProviderGroup[];
}

export interface ProviderGroup {
  providerReservationCode: string;
  purchases: PurchaseClientWithMetaWithTickets[];
  // tag for color-coding in UI
  colorIndex: number;
}

class CancelBookingManager {
  state = this._assignState();
  private _assignState() {
    return createGlobalState<{
      type: CancellationMethod | undefined;
      // ticketIds keyed by purchaseId
      selectedTicketsByPurchase: { [purchaseId: string]: string[] };
      // Provider groups whose actual refund exceeds 100% of the company value.
      refundOverage: { codes: string[] } | undefined;
    }>({
      type: 'customer_and_provider',
      selectedTicketsByPurchase: {},
      refundOverage: undefined,
    });
  }
  reset() {
    this.state = this._assignState();
  }
}

export const cancelBookingManager = new CancelBookingManager();

// --- Provider grouping ---

export const groupPurchasesByProvider = (
  purchases: PurchaseClientWithMetaWithTickets[],
): ProviderGroup[] => {
  const map = new Map<string, PurchaseClientWithMetaWithTickets[]>();
  for (const purchase of purchases) {
    const key = purchase.providerReservationCode || purchase._id;
    const existing = map.get(key);
    if (existing) {
      existing.push(purchase);
    } else {
      map.set(key, [purchase]);
    }
  }

  return Array.from(map.entries())
    .sort((a, b) => {
      // groups with more purchases first; then alphabetical
      if (b[1].length !== a[1].length) return b[1].length - a[1].length;
      return a[0].localeCompare(b[0]);
    })
    .map(([providerReservationCode, groupPurchases], colorIndex) => ({
      providerReservationCode,
      purchases: groupPurchases,
      colorIndex,
    }));
};

// --- Method intersection ---

export interface PurchaseAvailableMethods {
  visual: boolean;
  customer: boolean;
  provider: boolean;
  customer_and_provider: boolean;
}

export const getPurchaseAvailableMethods = (
  purchase: PurchaseClientWithMetaWithTickets,
): PurchaseAvailableMethods => {
  const providerVoided = !!purchase.void || !!purchase.voidProviderOnly;
  const customerVoided = !!purchase.void || !!purchase.voidCustomerOnly;
  const visuallyVoided =
    !!purchase.void || !!purchase.voidPseudo || customerVoided || providerVoided;
  return {
    visual: !visuallyVoided,
    customer: !customerVoided,
    provider: !providerVoided,
    customer_and_provider: !providerVoided && !customerVoided,
  };
};

export const getMethodsAcrossPurchases = (
  purchases: PurchaseClientWithMetaWithTickets[],
): PurchaseAvailableMethods => {
  if (purchases.length === 0) {
    return {
      visual: false,
      customer: false,
      provider: false,
      customer_and_provider: false,
    };
  }
  return purchases.map(getPurchaseAvailableMethods).reduce((acc, m) => ({
    visual: acc.visual && m.visual,
    customer: acc.customer && m.customer,
    provider: acc.provider && m.provider,
    customer_and_provider: acc.customer_and_provider && m.customer_and_provider,
  }));
};

// --- Protection detection ---

export const bookingHasProtection = (
  booking: BookingClientWithPurchasesAndTickets,
): boolean => {
  if (booking.bookingProtection) return true;
  if (booking.bookingKoala && booking.bookingKoala.status !== 'PENDING_DELETE') {
    return true;
  }
  return false;
};

// --- Calculation helpers ---

export const getGroupRefundDefault = (
  calculation: PurchaseGetCancelCalculationResponse | undefined,
  providerReservationCode: string,
): { valueInBookCcy: number; valueInOriginalCcy: number } => {
  const entry = calculation?.purchaseRefunds?.[providerReservationCode];
  return {
    valueInBookCcy: entry?.valueInBookCcy ?? 0,
    valueInOriginalCcy: entry?.valueInOriginalCcy ?? 0,
  };
};

// --- Submit assembly: fan group value across purchase ids ---

export const fanOutGroupValueToPurchases = (
  providerGroups: ProviderGroup[],
  groupValuesByCode: { [providerReservationCode: string]: number },
): { [purchaseId: string]: number } => {
  const out: { [purchaseId: string]: number } = {};
  for (const group of providerGroups) {
    const groupValue = groupValuesByCode[group.providerReservationCode] ?? 0;
    for (const purchase of group.purchases) {
      // The provider only knows the group total; per-purchase split is for the
      // request shape only and is not surfaced to the operator. Split evenly.
      out[purchase._id] = groupValue / group.purchases.length;
    }
  }
  return out;
};

// --- Color palette for provider groups ---
//
// SquarePill takes raw hex strings, not theme tokens — this palette is
// expressed directly in hex so it can be passed through verbatim.

export const PROVIDER_GROUP_COLORS = [
  { bg: '#D6E6FF', fg: '#0A66FF' }, // primary
  { bg: '#FFE3CC', fg: '#C95A00' }, // secondary
  { bg: '#E5D6FF', fg: '#7A33D9' }, // tertiary
  { bg: '#FFE9B3', fg: '#A86A00' }, // warning
  { bg: '#CDEFD7', fg: '#1F7A3A' }, // success
  { bg: '#FDC9B3', fg: '#C92A1A' }, // error
] as const;

export const getGroupColor = (colorIndex: number) =>
  PROVIDER_GROUP_COLORS[colorIndex % PROVIDER_GROUP_COLORS.length];
